import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "button", "status" ]
  static values = { url: String, type: String }

  connect() {
    // Remember the button's idle markup so we can restore it if the user navigates away
    // (geolocation redirect) and comes back — bfcache/Turbo would otherwise restore the
    // frozen "Locating..." disabled state.
    if (this.hasButtonTarget) this.originalButtonHTML = this.buttonTarget.innerHTML
    this.resetButton = this.resetButton.bind(this)
    window.addEventListener("pageshow", this.resetButton)
    document.addEventListener("turbo:load", this.resetButton)

    const params = new URLSearchParams(window.location.search)
    if (params.has("lat") && params.has("lng")) {
      if (this.hasStatusTarget) {
        this.statusTarget.innerHTML = `
          <div class="flex items-center justify-between bg-secondary-soft dark:bg-secondary-soft/10 border border-secondary/20 dark:border-secondary/25 p-3 rounded-xl text-xs text-secondary-dark dark:text-secondary font-semibold shadow-xs">
            <span class="flex items-center gap-1.5">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
              </span>
              Showing results nearest to your location
            </span>
            <a href="${window.location.pathname}" class="text-primary hover:underline hover:text-primary-active font-bold">Clear</a>
          </div>
        `
      }
    }
  }

  disconnect() {
    window.removeEventListener("pageshow", this.resetButton)
    document.removeEventListener("turbo:load", this.resetButton)
  }

  resetButton() {
    if (!this.hasButtonTarget || this.originalButtonHTML == null) return
    this.buttonTarget.disabled = false
    this.buttonTarget.innerHTML = this.originalButtonHTML
  }

  find(event) {
    event.preventDefault()
    
    if (!navigator.geolocation) {
      this.showError(window.MediFyrI18n?.t?.geolocation_unsupported || "Geolocation is not supported by your browser.")
      return
    }

    const btn = this.buttonTarget
    const originalText = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Locating...</span>
    `

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude

        const targetUrl = this.hasUrlValue && this.urlValue ? this.urlValue : window.location.pathname
        const sameTarget = !(this.hasUrlValue && this.urlValue)
        const urlParams = new URLSearchParams(sameTarget ? window.location.search : "")
        urlParams.set("lat", lat)
        urlParams.set("lng", lng)
        // Carry the page's entity type so /search shows the matching results (hospital page
        // -> hospitals, doctor -> doctors, procedure -> procedures).
        if (this.hasTypeValue && this.typeValue) urlParams.set("type", this.typeValue)

        window.location.href = targetUrl + "?" + urlParams.toString()
      },
      (error) => {
        btn.disabled = false
        btn.innerHTML = originalText
        
        let msg = window.MediFyrI18n?.t?.location_unavailable || "Unable to retrieve your location."
        if (error.code === error.PERMISSION_DENIED) {
          msg = window.MediFyrI18n?.t?.location_denied || "Location access denied. Please enable location permissions."
        }
        this.showError(msg)
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    )
  }

  showError(msg) {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="flex items-center gap-1.5 bg-rose-50 dark:bg-rose-950/10 border border-rose-200 dark:border-rose-900/30 p-3 rounded-xl text-xs text-rose-800 dark:text-rose-400 font-semibold shadow-xs">
          <svg class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/></svg>
          <span>${msg}</span>
        </div>
      `
    }
  }
}
